import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useGetIdentity, useTranslate } from "@refinedev/core";
import { Alert, Autocomplete, Box, Button, Divider, Stack, TextField, Typography } from "@mui/material";

import { NotificationFormValues, parseEmails } from "@modules/notifications/validations";
import { FcmUser, useFcmUsersQuery, useTestNotificationMutation } from "@modules/notifications/services/mutations";

export function ReviewStep() {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity<{ email?: string }>();
  const { getValues } = useFormContext<NotificationFormValues>();
  const values = getValues();
  const isPush = values.notification_type === "push";
  const [testEmail, setTestEmail] = useState("");
  const [testToken, setTestToken] = useState("");
  const [testUser, setTestUser] = useState<FcmUser | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const { data: fcmUsers = [], isFetching: loadingUsers } = useFcmUsersQuery(
    isPush ? values.agreement : null,
    userSearch,
  );
  const { mutate: sendTest, isPending, data: testResult, reset: resetTest } = useTestNotificationMutation();

  const t = (key: string, options?: any) => translate(`notifications_emails_admin.${key}`, options);

  const schedule =
    values.schedule_type === "one_time"
      ? values.date && new Date(values.date).toLocaleString("pt-BR")
      : `${(values.days_of_week ?? []).map((day) => t(`days.${day}`)).join(", ")} às ${values.time}`;

  const rows: Array<[string, React.ReactNode]> = [
    [t("fields.notification_type"), t(`types.${values.notification_type}`)],
    [t("fields.agreement"), values.agreement ? `#${values.agreement}` : "-"],
    isPush
      ? [t("fields.recipients"), t("instructions.push")]
      : [t("fields.recipients"), t("review.emailsCount", { count: parseEmails(values.emails_text).length })],
    [t("fields.subject"), isPush ? values.push_subject : values.email_subject],
    [t("fields.schedule_type"), `${t(`schedule.${values.schedule_type}`)} — ${schedule}`],
  ];

  return (
    <Stack spacing={3}>
      <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: 1.5, m: 0 }}>
        {rows.map(([label, value]) => (
          <Box key={label} sx={{ display: "contents" }}>
            <Typography component="dt" color="text.secondary">{label}</Typography>
            <Typography component="dd" sx={{ m: 0 }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      {isPush ? (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover", maxWidth: 360 }}>
          <Typography fontWeight={600}>{values.push_subject}</Typography>
          <Typography variant="body2">{values.push_body}</Typography>
        </Box>
      ) : (
        <Box
          sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, maxHeight: 400, overflow: "auto" }}
          dangerouslySetInnerHTML={{ __html: values.email_body ?? "" }}
        />
      )}

      <Divider />

      <Stack spacing={2}>
        <Typography variant="subtitle1" fontWeight={600}>{t("review.testTitle")}</Typography>
        <Alert severity="info">{t(isPush ? "review.testPushInfo" : "review.testEmailInfo")}</Alert>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          {isPush && (
            <Autocomplete
              size="small"
              sx={{ minWidth: 320 }}
              options={fcmUsers}
              loading={loadingUsers}
              value={testUser}
              onChange={(_, user) => setTestUser(user)}
              onInputChange={(_, text, reason) => reason === "input" && setUserSearch(text)}
              filterOptions={(options) => options}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(user) => `${user.full_name || user.email} (${user.platforms.join(", ")})`}
              noOptionsText={t("review.noFcmUsers")}
              renderInput={(params) => <TextField {...params} label={t("fields.test_user")} />}
            />
          )}
          {isPush && (
            <TextField
              size="small"
              label={t("fields.test_token")}
              value={testToken}
              onChange={(event) => setTestToken(event.target.value)}
              sx={{ minWidth: 320 }}
            />
          )}
          {!isPush && (
            <TextField
              size="small"
              type="email"
              label={t("fields.test_email")}
              placeholder={identity?.email}
              value={testEmail}
              onChange={(event) => setTestEmail(event.target.value)}
              sx={{ minWidth: 280 }}
            />
          )}
          <Button
            variant="outlined"
            loading={isPending}
            onClick={() => {
              resetTest();
              sendTest({ values: getValues(), testEmail: testEmail || identity?.email, testToken: testToken.trim() || undefined, testUser: testUser?.id });
            }}
          >
            {t(isPush ? "buttons.testPush" : "buttons.testEmail")}
          </Button>
        </Stack>
        {testResult?.summary && (
          <Alert severity={testResult.results?.some((r: any) => r.status === "error") ? "error" : "success"}>
            {testResult.summary}
          </Alert>
        )}
      </Stack>
    </Stack>
  );
}
